/** @fileoverview Publish button, publish confirmation, and live version card for the builder. */

import { useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, Globe, Loader2, Rocket } from 'lucide-react';

import { cn } from 'cn';
import { getHostedAppUrl } from '../../appMode/hostConfig';
import { getAppSystemBindings } from '../../lib/appSystems';
import type { ConnectedSystem } from '../../services/systemsApi';
import { publishFabrikApp } from '../../services/appsApi';
import { ApiError } from '../../services/platformAuth';
import type { FabrikAppSummary } from '../../types/apps';
import type { Tenant } from '../../types/identity';
import { getConnectedSystemLabel, getConnectedSystemLogoUrl } from '../ConnectedSystemDetails';
import { Badge } from '../ui/badge';
import { Button, buttonVariants } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent } from '../ui/popover';

type Props = {
  app?: FabrikAppSummary;
  tenant: Tenant;
  systems: ConnectedSystem[];
  onAppChange: (app: FabrikAppSummary) => void;
};

type PublishFailure = { fixable: boolean; message: string };

const COPIED_RESET_MS = 1800;

/** Removes the protocol so the address reads as plain text. */
const stripProtocol = (url: string) => url.replace(/^https?:\/\//, '');

function ConnectionStatus({ system }: { system: ConnectedSystem }) {
  const statusLabel =
    system.pingStatus === 'SUCCESS'
      ? 'Connected'
      : system.pingStatus === 'FAILED'
      ? 'Connection failed'
      : 'Status unknown';
  return (
    <span
      role="img"
      aria-label={`Connection status: ${statusLabel}`}
      title={statusLabel}
      className={cn(
        'size-2 shrink-0 rounded-full',
        system.pingStatus === 'SUCCESS' && 'bg-app-brand-strong',
        system.pingStatus === 'FAILED' && 'bg-app-danger',
        !system.pingStatus && 'bg-app-text-muted'
      )}
    />
  );
}

/** Renders the publish button, the confirmation dialog, and the live version card. */
export default function PublishControl({ app, tenant, systems, onAppChange }: Props) {
  const [publishing, setPublishing] = useState(false);
  const [systemMappings, setSystemMappings] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [failure, setFailure] = useState<PublishFailure>();
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const copiedTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(copiedTimerRef.current), []);

  const { publishedVersion, hasUnpublishedChanges, slug, name, id } = app ?? {};
  const appUrl = slug ? getHostedAppUrl({ slug, preview: false }) : '';
  const appAddress = stripProtocol(appUrl);
  const isPublished = Boolean(publishedVersion);
  const upToDate = isPublished && hasUnpublishedChanges === false;
  const label = isPublished ? 'Publish changes' : 'Publish';
  const systemBindings = getAppSystemBindings(app?.draft_definition);
  const availableSystemMappings = Object.fromEntries(
    Object.entries(systemMappings).filter(([sourceId, targetId]) => {
      const source = systems.find((system) => system.id === sourceId);
      return (
        systemBindings.some(({ systemId }) => systemId === sourceId) &&
        source &&
        systems.some((system) => system.id === targetId && system.code === source.code)
      );
    })
  );
  const hasUnavailableSystems = systemBindings.some(
    ({ systemId }) => !systems.some((system) => system.id === systemId)
  );

  const openConfirmation = () => {
    setFailure(undefined);
    setSystemMappings(app?.publishedVersion?.systemMappings ?? {});
    setConfirmOpen(true);
  };

  const closeConfirmation = (open: boolean) => {
    setConfirmOpen(open);
    if (!open) setSystemMappings({});
  };

  const selectSystem = (sourceSystemId: string, targetSystemId: string) => {
    setSystemMappings((current) => {
      if (sourceSystemId === targetSystemId) {
        const next = { ...current };
        delete next[sourceSystemId];
        return next;
      }
      return { ...current, [sourceSystemId]: targetSystemId };
    });
  };

  const renderSystemInfo = (system: ConnectedSystem) => (
    <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
      <ConnectionStatus system={system} />
      <span className="min-w-0 flex-1 truncate font-medium text-app-text">{system.name}</span>
      {system.environment && (
        <Badge variant="secondary" className="shrink-0">
          {system.environment}
        </Badge>
      )}
      {system.companyId && (
        <span className="hidden min-w-0 max-w-40 flex-1 truncate text-xs text-app-text-muted sm:inline">
          · Company ID: {system.companyId}
        </span>
      )}
    </span>
  );

  /** Publishes the saved draft and shows the result card. */
  const publish = async () => {
    if (!app || !id || publishing || hasUnavailableSystems) return;
    const mappings = availableSystemMappings;
    setConfirmOpen(false);
    setSystemMappings({});
    setFailure(undefined);
    setPublishing(true);
    try {
      onAppChange(await publishFabrikApp(tenant.tenant_id, id, mappings));
    } catch (error) {
      const fixable = error instanceof ApiError && [400, 409].includes(error.status ?? 0);
      setFailure({ fixable, message: fixable ? error.message : '' });
    } finally {
      setPublishing(false);
      setCardOpen(true);
    }
  };

  /** Copies the app link and shows a short confirmation. */
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopied(true);
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        size="sm"
        disabled={!app || publishing || upToDate}
        className={
          upToDate
            ? 'border border-app-border bg-app-surface-muted text-app-text-secondary disabled:opacity-100'
            : 'bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:opacity-100'
        }
        onClick={openConfirmation}
      >
        {publishing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Publishing…
          </>
        ) : upToDate ? (
          <>
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Published
          </>
        ) : (
          label
        )}
      </Button>

      <Popover open={cardOpen} onOpenChange={setCardOpen}>
        <PopoverContent
          anchor={buttonRef}
          align="end"
          sideOffset={8}
          className="w-80 gap-3 bg-app-surface-card p-3"
        >
          <div aria-live="polite" className="flex flex-col gap-1">
            {failure ? (
              <>
                <p className="font-semibold text-app-text">
                  {failure.fixable ? 'Fix this before publishing' : "Couldn't publish. Try again."}
                </p>
                {failure.fixable && failure.message && (
                  <p className="text-xs text-app-text-secondary">{failure.message}</p>
                )}
              </>
            ) : (
              publishedVersion && (
                <>
                  <p className="flex items-center gap-1.5 font-semibold text-app-text">
                    <Check className="h-4 w-4 text-app-branch" aria-hidden="true" />
                    Version {publishedVersion.versionNo} is live
                  </p>
                  {appAddress && (
                    <p className="break-all text-xs text-app-text-secondary">{appAddress}</p>
                  )}
                </>
              )
            )}
          </div>
          {!failure && appUrl && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`transition-colors duration-200 ${
                  copied
                    ? 'border-app-brand-strong/40 bg-app-brand-subtle text-app-brand-strong hover:bg-app-brand-subtle hover:text-app-brand-strong'
                    : ''
                }`}
                onClick={copyLink}
              >
                {/* Keyed so the icon replays its entrance animation each time the state flips. */}
                {copied ? (
                  <Check
                    key="copied"
                    className="h-3.5 w-3.5 animate-in fade-in zoom-in-50 duration-200 motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : (
                  <Copy
                    key="copy"
                    className="h-3.5 w-3.5 animate-in fade-in duration-200 motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                <span aria-live="polite">{copied ? 'Copied' : 'Copy link'}</span>
              </Button>
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Open app
              </a>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={confirmOpen} onOpenChange={closeConfirmation}>
        <DialogContent className="grid-cols-1 max-h-screen overflow-x-hidden overflow-y-auto sm:max-w-2xl">
          <DialogHeader className="flex-row items-start gap-3 pr-6">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-app-brand-subtle text-app-brand-strong">
              <Rocket className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <DialogTitle className="text-base leading-snug font-semibold text-app-text">
                {publishedVersion ? 'Publish changes to' : 'Publish'} &quot;{name}&quot;?
              </DialogTitle>
              <DialogDescription className="text-app-text-muted">
                {publishedVersion
                  ? `Everyone using it will see version ${publishedVersion.versionNo + 1}.`
                  : `Everyone in ${tenant.name} will be able to open it.`}
              </DialogDescription>
            </div>
          </DialogHeader>
          {appAddress && (
            <div className="flex min-w-0 items-center gap-2 rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-xs text-app-text-secondary">
              <Globe className="h-3.5 w-3.5 shrink-0 text-app-text-muted" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate" title={appAddress}>
                {appAddress}
              </span>
            </div>
          )}
          {systemBindings.length > 0 && (
            <section aria-label="Connected systems" className="flex min-w-0 flex-col gap-2">
              <div>
                <h3 className="m-0 text-sm font-semibold text-app-text">Connected systems</h3>
                <p className="m-0 mt-1 text-xs text-app-text-muted">
                  Choose the systems to use for this published version. Your draft will not change.
                </p>
              </div>
              <div className="flex min-w-0 max-h-80 flex-col divide-y divide-app-border overflow-y-auto pr-1">
                {systemBindings.map(({ systemId }) => {
                  const sourceSystem = systems.find((candidate) => candidate.id === systemId);
                  if (!sourceSystem) {
                    return (
                      <div key={systemId} className="flex min-w-0 items-center gap-3 py-2">
                        <span aria-hidden="true" className="w-32 shrink-0 sm:w-52" />
                        <span className="min-w-0 flex-1 text-sm text-app-danger">
                          System unavailable
                        </span>
                      </div>
                    );
                  }
                  const sameTypeSystems = systems.filter(
                    (candidate) => candidate.code === sourceSystem.code
                  );
                  const selectedSystemId = availableSystemMappings[systemId] ?? systemId;
                  const selectedSystem =
                    sameTypeSystems.find((candidate) => candidate.id === selectedSystemId) ??
                    sourceSystem;
                  return (
                    <div key={systemId} className="flex min-w-0 items-center gap-3 py-2">
                      <div className="flex h-9 w-32 min-w-0 shrink-0 items-center sm:w-52">
                        <img
                          src={getConnectedSystemLogoUrl(sourceSystem.code)}
                          alt={getConnectedSystemLabel(sourceSystem.code)}
                          className="h-5 max-w-full shrink-0 object-contain object-left"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      {sameTypeSystems.length > 1 ? (
                        <Select
                          value={selectedSystemId}
                          disabled={publishing}
                          onValueChange={(value) => {
                            if (value) selectSystem(systemId, value);
                          }}
                        >
                          <SelectTrigger
                            aria-label={`System for ${getConnectedSystemLabel(sourceSystem.code)}`}
                            className="h-9 min-w-0 flex-1"
                          >
                            <SelectValue className="min-w-0">
                              {(value: string | null) => {
                                const valueSystem = sameTypeSystems.find(
                                  (candidate) => candidate.id === value
                                );
                                return valueSystem
                                  ? renderSystemInfo(valueSystem)
                                  : 'Select a system';
                              }}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent
                            align="start"
                            alignItemWithTrigger={false}
                            className="max-h-72"
                          >
                            <SelectGroup>
                              <SelectLabel className="px-2.5 py-2">
                                Connected {getConnectedSystemLabel(sourceSystem.code)} systems
                              </SelectLabel>
                              {sameTypeSystems.map((candidate) => (
                                <SelectItem
                                  key={candidate.id}
                                  value={candidate.id}
                                  className="min-h-10 py-2 pl-2.5 pr-8"
                                >
                                  <span className="flex min-w-0 flex-1 items-center gap-3">
                                    <ConnectionStatus system={candidate} />
                                    <span className="min-w-0 flex-1 truncate">
                                      {candidate.name}
                                    </span>
                                    <Badge variant="secondary">
                                      {candidate.environment || 'Environment not set'}
                                    </Badge>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex h-9 min-w-0 flex-1 items-center rounded-md border border-app-border px-3">
                          {renderSystemInfo(selectedSystem)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {hasUnavailableSystems && (
                <p role="alert" className="m-0 text-xs text-app-danger">
                  Reconnect unavailable systems before publishing.
                </p>
              )}
            </section>
          )}
          <DialogFooter className="-mx-4 -mb-4 bg-app-surface-muted/60">
            <Button type="button" variant="outline" onClick={() => closeConfirmation(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={hasUnavailableSystems || publishing}
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover"
              onClick={() => void publish()}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
